import React, { useState, useCallback, useEffect } from 'react'
import { Flex, Box } from 'reflexbox/styled-components'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'

import Card from 'components/Card'
import Button from 'components/Button'
import Loader from 'components/Loader'
import { TermsModal } from 'components/Modal'
import { TextField } from 'components/Form/Fields'
import { Text, Paragraph } from 'components/Typography'
import PublicPageWrapper from 'components/PublicPagesWrapper'

import logo from 'images/guigo_partners_black.svg'

import { CNPJMask, phoneMask } from 'helpers/masks'
import { userSignUp, checkDocument, getTermsOfUse } from 'services/partners'
import { checkEmail } from 'services/users'

import { Link, SignUpTitle } from './styles'
import theme from 'styles/theme'
import PasswordFormatValidation from 'components/PasswordFormatValidation'

Yup.setLocale({
  mixed: {
    required: 'Campo obrigatório',
  },
})

const FirstStepValidation = Yup.object().shape({
  cnpj: Yup.string()
    .min(18, 'CNPJ inválido')
    .required(),
  company_name: Yup.string()
    .min(2, 'Campo deve possuir no mínimo dois caracteres')
    .max(50, 'Campo deve possuir no máximo 50 caracteres')
    .matches(/[a-zA-Z0-9\.] [a-zA-Z0-9]/, 'Razão Social deve possuir no mínimo duas palavras')
    .required(),
  responsible_name: Yup.string()
    .min(2, 'Campo deve possuir no mínimo dois caracteres')
    .matches(/[a-zA-Z0-9\.] [a-zA-Z0-9]/, 'Nome deve possuir no mínimo duas palavras')
    .required(),
  phone: Yup.string()
    .min(14, 'Telefone inválido')
    .required(),
  email: Yup.string()
    .email()
    .required(),
  confirm_email: Yup.string()
    .oneOf([Yup.ref('email')], 'Os emails devem ser iguais')
    .required(),
  password: Yup.string()
    .min(6, 'A senha precisa conter os itens abaixo para ser válida.')
    .matches(/\w*[A-Za-z]\w*/, 'A senha precisa conter os itens abaixo para ser válida.')
    .matches(/.*[0-9].*/, 'A senha precisa conter os itens abaixo para ser válida.')
    .required('A senha precisa conter os itens abaixo para ser válida.'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password')], 'As senhas devem ser iguais')
    .required(),
})

const firstStepInitialValues = {
  cnpj: '',
  company_name: '',
  responsible_name: '',
  phone: '',
  email: '',
  confirm_email: '',
  password: '',
  confirm_password: '',
}

const SignUp = () => {
  const [isSubmiting, setIsSubmiting] = useState(false)
  const [userInfo, setUserInfo] = useState({})
  const [isTermsModalOpened, setIsTermsModalOpened] = useState(false)
  const [isRegistrationSent, setRegistrationSent] = useState(false)
  const [terms, setTerms] = useState({ text: null, file: null })

  const modalResponse = useCallback(({ accept_terms }) => handleSignUp({ accept_terms }), [userInfo])

  const handleSubmitForm = useCallback(async values => {
    try {
      setIsSubmiting(true)

      await checkDocument({ document: values?.cnpj.replace(/\D+/g, '') })
      await checkEmail(values.email)

      setIsTermsModalOpened(true)

      setUserInfo(values)
    } catch (e) {
      console.log(e)
      switch (e?.response.status) {
        case 400: {
          if (Object.keys(e?.response.data).includes('email')) {
            return toast.error('E-mail inválido!')
          }

          if (Object.keys(e?.response.data).includes('document')) {
            return toast.error('CNPJ inválido!')
          }
        }

        case 409: {
          const { detail } = e?.response.data
          if (detail.includes('E-mail')) {
            return toast.error('Este email já foi cadastrado!')
          }

          if (detail.includes('Document')) {
            return toast.error('Este CNPJ já foi cadastrado!')
          }
        }

        default: {
          return toast.error('Por favor, verifique a suas informações de cadastro.')
        }
      }
    } finally {
      setIsSubmiting(false)
    }
  })

  const handleSignUp = useCallback(
    async ({ accept_terms }) => {
      try {
        setIsSubmiting(true)

        const { phone, cnpj, responsible_name, company_name, email, password } = userInfo

        const formattedData = {
          user: { email, password },
          company_name,
          responsible_name,
          document: cnpj.replace(/\D+/g, ''),
          phones: [{ number: phone }],
          is_contract_accepted: accept_terms,
        }

        await userSignUp(formattedData)

        setIsSubmiting(false)
        toast.success('Cadastro realizado com sucesso!')
        setIsTermsModalOpened(false)
        setRegistrationSent(true)
      } catch (error) {
        console.log(error)
        toast.error('Não foi possivel realizar o seu cadastro no momento.')
        setIsSubmiting(false)
      }
    },
    [userInfo],
  )

  useEffect(() => {
    const getTerms = async () => {
      try {
        const { data } = await getTermsOfUse()
        setTerms({ text: data.text, file: data.file })
      } catch (e) {
        console.log(e)
        toast.error('Não foi possivel carregar os termos de uso no momento.')
      }
    }

    getTerms()
  }, [])

  return (
    <PublicPageWrapper>
      <Card maxWidth='614px' minWidth='384px' bg={theme.colors.white}>
        <Flex flexDirection='column'>
          <Flex mb={32} justifyContent='center'>
            <img src={logo} />
          </Flex>

          {isRegistrationSent ? (
            <Flex flexDirection='column' alignItems='center'>
              <SignUpTitle color={theme.colors.turquoises.T300} as='h4'>
                Pré cadastro enviado!
              </SignUpTitle>
              <Paragraph style={{ textAlign: 'center' }}>
                Seu pré cadastro foi finalizado e enviado com sucesso! Em breve você receberá um link no seu email para
                concluir o seu cadastro.
              </Paragraph>

              <Button as={Link} to='/' size='auto' mt='32px'>
                Voltar para a página inicial
              </Button>
            </Flex>
          ) : (
            <>
              <Box mb={32}>
                <SignUpTitle color={theme.colors.turquoises.T300} as='h4'>
                  Criar pré cadastro
                </SignUpTitle>
              </Box>

              <Formik
                initialValues={firstStepInitialValues}
                onSubmit={handleSubmitForm}
                validationSchema={FirstStepValidation}
              >
                {({ values }) => (
                  <Form>
                    <TextField
                      name='company_name'
                      type='text'
                      label='Razão Social'
                      placeholder='Razão Social'
                      mb={32}
                    />
                    <TextField
                      name='cnpj'
                      type='text'
                      label='CNPJ'
                      placeholder='CNPJ'
                      mask={CNPJMask}
                      guide={false}
                      mb={32}
                    />
                    <TextField
                      name='phone'
                      type='text'
                      label='Telefone'
                      placeholder='Telefone'
                      guide={false}
                      mask={phoneMask}
                      mb={32}
                    />
                    <TextField
                      name='responsible_name'
                      type='text'
                      label='Nome do Responsável'
                      placeholder='Nome do Responsável'
                      mb={32}
                    />
                    <TextField name='email' type='email' label='email' placeholder='E-mail' mb={32} />
                    <TextField name='confirm_email' label='Confirmar email' placeholder='Confirmar e-mail' mb={32} />
                    <TextField name='password' type='password' label='Senha' placeholder='Senha' mb={24} />
                    {PasswordFormatValidation(values?.password)}
                    <TextField
                      name='confirm_password'
                      type='password'
                      label='Confirmar senha'
                      placeholder='Confirmar senha'
                      mb={32}
                    />
                    <Button size='auto' disabled={isSubmiting} type='submit'>
                      {isSubmiting ? <Loader /> : 'Solicitar'}
                    </Button>
                  </Form>
                )}
              </Formik>
            </>
          )}
          {!isRegistrationSent && (
            <Box mt='24px'>
              <Text color={theme.colors.blues.A800}>
                Já cadastrado? &nbsp;
                <Link color={theme.colors.blues.A800} to='/login'>
                  Acesse aqui
                </Link>
              </Text>
            </Box>
          )}
        </Flex>
      </Card>
      <TermsModal
        isOpened={isTermsModalOpened}
        setIsOpened={setIsTermsModalOpened}
        action={modalResponse}
        isLoading={isSubmiting}
        termsText={terms.text}
        termsPDF={terms.file}
      />
    </PublicPageWrapper>
  )
}

export default SignUp
